<script setup lang="ts">
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import StatusBadge from "@comp-lib/components/StatusBadge.vue";
import type { NavLink } from "@/types";

const props = defineProps<{
  link: NavLink;
}>();

const route = useRoute();
const isOpen = ref(false);

function checkIfChildActive() {
  if (!props.link.children) return;
  isOpen.value = props.link.children.some((child) => {
    // Router match
    if (child.routerName && child.routerName === route.name) {
      return true;
    }
    // External URL match
    if (child.externalUrl && child.externalUrl === window.location.href) {
      return true;
    }
    return false;
  });
}

checkIfChildActive();

watch(() => route.name, () => {
  checkIfChildActive();
});
</script>

<template>
  <div class="sidebar-menu-link" @click="isOpen = !isOpen">
    <span class="icon">
      <FontAwesomeIcon v-if="props.link.icon" :icon="props.link.icon" />
    </span>
    {{ props.link.displayName }}
    <span :class="['chevron', { open: isOpen }]">
      <FontAwesomeIcon :icon="faChevronDown" />
    </span>
  </div>

  <div :class="['submenu', { open: isOpen }]">
    <div class="children">
      <template v-for="child in props.link.children" :key="child.displayName">
        <RouterLink
          v-if="child.routerName && (!child.routerParams || Object.values(child.routerParams).every(val => !!val))"
          :to="{ 
            name: child.routerName,
            params: child.routerParams || {}
          }"
          :class="{ active: route.name === child.routerName }"
        >
          {{ child.displayName }}
          <StatusBadge v-if="child.beta" text="Beta" color="teal" />
        </RouterLink>

        <a
          v-else-if="child.externalUrl"
          :href="child.externalUrl"
          target="_blank"
        >
          {{ child.displayName }}
        </a>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.sidebar-menu-link {
  @include g.sidebarLinkStyles;
  user-select: none;
  .chevron {
    font-size: .875em;
    margin-top: 2px;
    transition: transform .2s;
    &.open {
      transform: rotate(180deg);
    }
  }
}
.submenu {
  visibility: hidden;
  opacity: 0;
  display: grid;
  grid-template-rows: 0fr;
  padding-left: 45px;
  transition: .2s;
  &.open {
    visibility: visible;
    opacity: 1;
    grid-template-rows: 1fr;
    padding-bottom: 10px;
  }
  .children {
    overflow: hidden;
    a {
      @include g.sidebarLinkStyles;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 20px;
      font-weight: 400;
      color: inherit;
      border-radius: map.get(v.$border-radius, "max");
    }
  }
}
</style>
